import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { useSite } from "@/lib/site";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

function Field({ label, children, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <label className="tech-label">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-zinc-300 p-5 sm:p-6">
      <h3 className="font-display font-extrabold text-lg tracking-tight mb-5">{title}</h3>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export default function SiteEditor({ onSaved }) {
  const { site } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (site) setForm({ ...site });
  }, [site]);

  if (!form) return <div className="tech-label">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBi = (k, lang, v) =>
    setForm((f) => ({ ...f, [k]: { ...(f[k] || {}), [lang]: v } }));

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/site", form);
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between sticky top-16 bg-zinc-50 py-3 z-10 -mt-3">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight">Site Content</h2>
          <div className="tech-label">Edit logo, hero, contact &amp; SEO</div>
        </div>
        <Button onClick={onSave} disabled={saving} className="bg-brand-red hover:bg-brand-redHover rounded-none" data-testid="site-save">
          <Save size={14} className="mr-2" /> {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>

      <Section title="Brand & Logo">
        <Field label="Business name" testId="field-business-name">
          <Input
            data-testid="input-business-name"
            value={form.business_name || ""}
            onChange={(e) => set("business_name", e.target.value)}
            className="rounded-none h-10"
          />
        </Field>
        <ImageUpload
          testId="logo-upload"
          label="Logo (used in navbar, hero & social share image)"
          value={form.logo_url}
          onChange={(v) => set("logo_url", v)}
        />
        <ImageUpload
          testId="hero-bg-upload"
          label="Hero background image"
          value={form.hero_bg_url}
          onChange={(v) => set("hero_bg_url", v)}
        />
      </Section>

      <Section title="Hero text">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Hero title (English)">
            <Textarea
              data-testid="input-hero-title-en"
              rows={2}
              value={form.hero_title?.en || ""}
              onChange={(e) => setBi("hero_title", "en", e.target.value)}
              className="rounded-none"
            />
          </Field>
          <Field label="Hero title (Español)">
            <Textarea
              data-testid="input-hero-title-es"
              rows={2}
              value={form.hero_title?.es || ""}
              onChange={(e) => setBi("hero_title", "es", e.target.value)}
              className="rounded-none"
            />
          </Field>
          <Field label="Hero subtitle (English)">
            <Textarea
              rows={2}
              value={form.hero_subtitle?.en || ""}
              onChange={(e) => setBi("hero_subtitle", "en", e.target.value)}
              className="rounded-none"
            />
          </Field>
          <Field label="Hero subtitle (Español)">
            <Textarea
              rows={2}
              value={form.hero_subtitle?.es || ""}
              onChange={(e) => setBi("hero_subtitle", "es", e.target.value)}
              className="rounded-none"
            />
          </Field>
        </div>
      </Section>

      <Section title="About">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="About title (English)">
            <Input value={form.about_title?.en || ""} onChange={(e) => setBi("about_title", "en", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="About title (Español)">
            <Input value={form.about_title?.es || ""} onChange={(e) => setBi("about_title", "es", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="About body (English)">
            <Textarea rows={4} value={form.about_body?.en || ""} onChange={(e) => setBi("about_body", "en", e.target.value)} className="rounded-none" />
          </Field>
          <Field label="About body (Español)">
            <Textarea rows={4} value={form.about_body?.es || ""} onChange={(e) => setBi("about_body", "es", e.target.value)} className="rounded-none" />
          </Field>
        </div>
      </Section>

      <Section title="Contact info">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Primary phone">
            <Input data-testid="input-phone-primary" value={form.phone_primary || ""} onChange={(e) => set("phone_primary", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="Secondary phone">
            <Input value={form.phone_secondary || ""} onChange={(e) => set("phone_secondary", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="WhatsApp (digits only, with country code)">
            <Input data-testid="input-whatsapp" value={form.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} className="rounded-none h-10" placeholder="12015226155" />
          </Field>
          <Field label="Email">
            <Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="Address">
            <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="Google Maps embed URL">
            <Input value={form.google_maps_embed || ""} onChange={(e) => set("google_maps_embed", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="Hours (English)">
            <Input value={form.hours?.en || ""} onChange={(e) => setBi("hours", "en", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="Horario (Español)">
            <Input value={form.hours?.es || ""} onChange={(e) => setBi("hours", "es", e.target.value)} className="rounded-none h-10" />
          </Field>
        </div>
      </Section>

      <Section title="SEO & Google Ads">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="SEO title">
            <Input value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className="rounded-none h-10" />
          </Field>
          <Field label="Google Tag / GA ID (gtag)">
            <Input data-testid="input-gtag" value={form.google_tag_id || ""} onChange={(e) => set("google_tag_id", e.target.value)} className="rounded-none h-10" placeholder="G-XXXXXXX or AW-XXXXXXX" />
          </Field>
          <Field label="SEO description">
            <Textarea rows={3} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className="rounded-none" />
          </Field>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving} className="bg-brand-red hover:bg-brand-redHover rounded-none">
          <Save size={14} className="mr-2" /> {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
